/*
 *** Usage in FO: OK
 *** Usage in PO: OK
 *** Usage in BO: OK
 */
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { Skeleton } from '@hivebrite/design-system/Skeleton';
import type { UsersAvatarListUser } from '@hivebrite/design-system/UsersAvatarList';
import { UsersAvatarList } from '@hivebrite/design-system/UsersAvatarList';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import type { Event } from 'hb-react/shared/apps/Events/types';
import { useGetEventsAttendeesQuery } from 'hb-react/shared/components/DesignSystem/Attendees/hooks';
import UsersModal from 'hb-react/shared/components/DesignSystem/UsersModal';

const StaticAttendees = ({
  attendees,
  totalAttendees,
  totalExternalAttendees = 0,
  imgSize = 32,
  eventId,
  eventType,
  maxAvatars = 4,
  label,
}: AttendeesPropsStatic) => {
  const { currentUser } = useDefaultAppContexts();
  const [usersModalIsOpen, setUsersModalIsOpen] = useState(false);
  const modalTitle = useMemo(() => {
    let title = I18n.t('web.networks.events.show.attenders');

    if (totalExternalAttendees > 0) {
      title += ` (${I18n.t('web.networks.events.show.external_attendees', {
        count: totalExternalAttendees,
      })})`;
    }

    return title;
  }, [totalExternalAttendees]);

  const showModal = (event) => {
    event?.stopPropagation();
    setUsersModalIsOpen(true);
  };
  const hideModal = (event) => {
    event?.stopPropagation();
    setUsersModalIsOpen(false);
  };

  const fetchUsers = (eventType) => {
    if (eventType === 'event' && currentUser) {
      return Routes.attendees_user_events_path(currentUser.id, {
        event_id: eventId,
      });
    }

    return Routes.network_event_attendees_path(eventId);
  };

  return (
    <>
      <UsersAvatarList
        imgSize={imgSize}
        users={attendees}
        maxAvatars={maxAvatars}
        totalUsers={totalAttendees}
        onClickMore={currentUser ? showModal : undefined}
        screenReadersListTitle={s__('Accessibility|Attendees')}
        screenReadersMoreButtonText={s__('Accessibility|View all attendees')}
        children={label?.({ totalAttendees, totalExternalAttendees })}
      />
      {usersModalIsOpen && (
        <UsersModal
          onCloseModal={hideModal}
          routes={{ fetchUsers: fetchUsers(eventType) }}
          title={modalTitle}
          screenReadersCloseButtonText={s__(
            'Accessibility|Close the attendees list window',
          )}
        />
      )}
    </>
  );
};

const LazyAttendees = (props: AttendeesProps) => {
  const { eventId, eventType } = props;

  const { data, isLoading } = useGetEventsAttendeesQuery({
    id: eventId,
    type: eventType,
  });

  if (isLoading) {
    return (
      <Box
        flexDirection="column"
        dataTestId="loader"
        minHeight={40}
        width="100%"
      >
        <Skeleton width="100%" mt={2} display="block" />
        <Skeleton width="100%" mt={2} display="block" />
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  const { data: attendees, total = 0, totalExternal = 0 } = data;

  const totalAttendees = total + totalExternal;

  return (
    total > 0 && (
      <StaticAttendees
        {...props}
        totalAttendees={totalAttendees}
        totalExternalAttendees={totalExternal}
        attendees={attendees}
      />
    )
  );
};

export interface AttendeesPropsStatic {
  attendees: UsersAvatarListUser[];
  totalAttendees: number;
  totalExternalAttendees: number;
  imgSize?: number;
  eventId: Event['id'];
  eventType: Event['type'];
  maxAvatars?: number;
  label?: (props: {
    totalAttendees: number;
    totalExternalAttendees: number;
  }) => ReactNode;
}

interface AttendeesPropsLazy
  extends Omit<
    AttendeesPropsStatic,
    'attendees' | 'totalAttendees' | 'totalExternalAttendees'
  > {
  lazy: boolean;
}

type AttendeesProps = AttendeesPropsStatic | AttendeesPropsLazy;

const isAttendeesPropsLazy = (item): item is AttendeesPropsLazy =>
  'lazy' in item && item.lazy;

const Attendees = (props: AttendeesProps) => {
  if (isAttendeesPropsLazy(props)) {
    return <LazyAttendees {...props} />;
  }

  return <StaticAttendees {...(props as AttendeesPropsStatic)} />;
};

export default Attendees;
